/**
 * Phase 9.7.2 — ✨ Feature Boost UI Chip
 * --------------------------------------
 * Single-source UI badge that surfaces the platform's exposure boost on
 * any featured track. Reads `boost_applied` + `floor_applied` from a
 * submission and renders an optional reach number alongside.
 *
 * Variants:
 *   • `chip`    — small inline pill ("✨ FEATURE BOOST")
 *   • `meter`   — chip + boosted-reach count ("✨ FEATURE BOOST · 2.1K reach")
 *   • `banner`  — full-width banner for TrackPage / hero placements
 *
 * Visual style: amber→pink gradient, soft glow, all-caps tracking-wide.
 * Aligns with FeaturedNowBanner + RecapStoryCard branding.
 */
const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return (v / 1000).toFixed(v >= 10000 ? 0 : 1).replace(/\.0$/, "") + "K";
  return String(v);
};

const reachFor = (submission) => {
  return (
    Number(submission?.boost_applied || 0) +
    Number(submission?.floor_applied || 0)
  );
};

// Phase 14.1 — campaign lifecycle helpers. Pure functions, safe on
// legacy tracks that don't have the new fields.
const featureRemainingText = (submission) => {
  if (!submission?.feature_active) return null;
  const secs = Number(submission?.feature_remaining_seconds || 0);
  if (secs <= 0) return null;
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  if (days >= 1) return `~${days}d ${hours}h left`;
  const mins = Math.floor((secs % 3600) / 60);
  return hours >= 1 ? `~${hours}h ${mins}m left` : `~${mins}m left`;
};
const featureProgressPct = (submission) =>
  Math.max(0, Math.min(100, Number(submission?.feature_progress_pct || 0)));

// Phase 9.7.7 — Estimated impressions ≈ reach × CTR_MULTIPLIER.
// Bumped from 6× to 8× to reflect the increased rotation density:
// featured tracks now occupy 60% of the discover mix (was 50%), get a
// dedicated Featured Push Today rail at the top of /home, and benefit
// from the 20s drip cron — so a single reach unit surfaces across
// noticeably more rotation slots than before.
const IMPRESSION_MULTIPLIER = 8;
const impressionsFor = (submission) => Math.round(reachFor(submission) * IMPRESSION_MULTIPLIER);

const FeatureBoostChip = ({ submission, variant = "chip", className = "" }) => {
  if (!submission) return null;
  // Show the chip for genuinely featured or spotlight tracks. Other
  // statuses shouldn't expose the badge even if boost_applied carries
  // legacy data.
  const isSpotlight = submission.status === "spotlight";
  const isFeatured = submission.status === "featured";
  if (!isFeatured && !isSpotlight) return null;

  const reach = reachFor(submission);
  const impressions = impressionsFor(submission);

  // Phase 9.7.5 — Spotlight gets its own purple→amber gradient + "SPOTLIGHT
  // SURGE ACTIVE" copy so the prestige tier reads instantly different from
  // Feature.
  const palette = isSpotlight
    ? {
        gradient: "from-purple-500/25 via-amber-400/20 to-pink-500/25",
        border: "border-purple-400/50",
        text: "text-amber-100",
        accent: "text-purple-100",
        label: "Spotlight Surge Active",
        emoji: "🌟",
        chipLabel: "Spotlight",
        bannerSub: "Weekly Spotlight Push — handpicked + amplified by RGM Studio",
      }
    : {
        gradient: "from-amber-500/20 via-pink-500/15 to-amber-500/20",
        border: "border-amber-400/40",
        text: "text-amber-200",
        accent: "text-amber-100",
        label: "Feature Boost Active",
        emoji: "✨",
        chipLabel: "Feature Boost",
        bannerSub: "Currently being promoted by RGM Studio — added discovery placements + boosted reach.",
      };

  if (variant === "banner") {
    return (
      <div
        data-testid={isSpotlight ? "spotlight-surge-banner" : "feature-boost-banner"}
        className={`px-4 py-3 rounded-xl bg-gradient-to-r ${palette.gradient} border ${palette.border} flex items-center gap-3 flex-wrap ${className}`}
      >
        <span className="text-2xl">{palette.emoji}</span>
        <div className="flex-1 min-w-[180px]">
          <p className={`text-sm font-black uppercase tracking-wider ${palette.text}`}>
            {palette.label}
          </p>
          <p className={`text-[11px] ${palette.accent}/85 mt-0.5`}>
            {palette.bannerSub}
          </p>
          {/* Phase 14.1 — Active Feature Surge progress + remaining time. */}
          {submission.feature_active && (
            <div
              className="mt-2 flex items-center gap-2"
              data-testid="feature-surge-progress"
            >
              <div className="flex-1 max-w-[180px] h-1.5 bg-black/50 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${palette.gradient} transition-[width] duration-700`}
                  style={{ width: `${featureProgressPct(submission)}%` }}
                />
              </div>
              <span
                className={`text-[10px] font-bold ${palette.accent}/90 whitespace-nowrap`}
                data-testid="feature-surge-remaining"
              >
                {featureProgressPct(submission)}%
                {featureRemainingText(submission)
                  ? ` · ${featureRemainingText(submission)}`
                  : ""}
              </span>
            </div>
          )}
        </div>
        {reach > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="feature-boost-reach">
            <span
              className={`px-3 py-1 rounded-full bg-black/40 border ${palette.border} ${palette.accent} text-xs font-bold`}
              title="Boosted exposure delivered so far"
            >
              {palette.emoji} {fmt(reach)} reach
            </span>
            <span
              className="px-3 py-1 rounded-full bg-black/40 border border-pink-400/40 text-pink-100 text-xs font-bold"
              data-testid="feature-boost-impressions"
              title="Estimated impressions across discovery surfaces (reach × 8)"
            >
              👁 {fmt(impressions)} impressions
            </span>
          </div>
        )}
      </div>
    );
  }

  if (variant === "meter") {
    return (
      <span
        data-testid={isSpotlight ? "spotlight-surge-meter" : "feature-boost-meter"}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${palette.gradient} border ${palette.border} ${palette.accent} text-[10px] font-black uppercase tracking-wider ${className}`}
      >
        <span>{palette.emoji}</span>
        <span>{isSpotlight ? "SPOTLIGHT" : "BOOST"}</span>
        {reach > 0 && (
          <span className="text-white normal-case font-bold tracking-normal">
            · {fmt(reach)} reach
          </span>
        )}
      </span>
    );
  }

  // Default — small chip
  return (
    <span
      data-testid={isSpotlight ? "spotlight-surge-chip" : "feature-boost-chip"}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r ${palette.gradient} border ${palette.border} ${palette.accent} text-[10px] font-black uppercase tracking-wider ${className}`}
      title={`${palette.label} — promoted by RGM Studio`}
    >
      {palette.emoji} {palette.chipLabel}
    </span>
  );
};

export { reachFor, fmt as fmtReach };
export default FeatureBoostChip;
